import React from 'react';
import { Home, Waves, Droplets, Sun, Building2, Truck, Trash2, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
const RESIDENTIAL_SERVICES = [{
  title: 'Water Heaters',
  description: 'Installation, repair, and maintenance of traditional and tankless water heaters.',
  icon: Zap
}, {
  title: 'Drain Cleaning',
  description: 'Professional drain snaking and hydro jetting to clear the toughest clogs.',
  icon: Waves
}, {
  title: 'Fixture Installation',
  description: 'Upgrade your kitchen and bathroom with professional faucet, sink, and toilet installations.',
  icon: Droplets
}, {
  title: 'Leak Repair',
  description: 'Fast and reliable leak detection and repair to protect your home from water damage.',
  icon: Home
}];
const COMMERCIAL_SERVICES = [{
  title: 'Commercial Plumbing',
  description: 'Full-service plumbing solutions for restaurants, retail stores, and office buildings.',
  icon: Building2
}, {
  title: 'Sewer Line Services',
  description: 'Comprehensive sewer line inspections, repairs, and replacements.',
  icon: Trash2
}, {
  title: 'Preventative Maintenance',
  description: 'Keep your business running smoothly with scheduled plumbing maintenance plans.',
  icon: Truck
}];
export function Services() {
  return <section id="services" className="py-24 bg-dark relative overflow-hidden" data-id="element-223">
      <div className="absolute top-0 right-0 opacity-5 pointer-events-none transform translate-x-1/3 -translate-y-1/3" data-id="element-224">
        <svg width="600" height="600" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" data-id="element-225">
          <path fill="#dc2626" d="M45.7,-76.4C58.9,-69.3,69.1,-55.3,77.5,-40.5C85.9,-25.7,92.5,-10.1,90.4,4.6C88.3,19.3,77.5,33.1,65.8,44.3C54.1,55.5,41.5,64.1,27.3,70.5C13.1,76.9,-2.7,81.1,-18.2,78.9C-33.7,76.7,-48.9,68.1,-60.8,56.1C-72.7,44.1,-81.3,28.7,-84.5,12.3C-87.7,-4.1,-85.5,-21.5,-77.3,-36.1C-69.1,-50.7,-54.9,-62.5,-40.1,-68.9C-25.3,-75.3,-9.9,-76.3,6.3,-84.1C22.5,-91.9,32.5,-83.5,45.7,-76.4Z" transform="translate(100 100)" data-id="element-226" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-227">
        <div className="flex flex-col lg:flex-row gap-12 mb-16 items-center" data-id="element-228">
          <div className="lg:w-1/2 text-left" data-id="element-229">
            <h2 className="font-display text-4xl md:text-5xl text-white uppercase tracking-wide mb-4" data-id="element-230">
              Expert Plumbing for{' '}
              <span className="text-accent" data-id="element-231">Every Need</span>
            </h2>
            <p className="text-slate-400 text-lg" data-id="element-232">
              From leaky faucets to commercial water heaters, we have the
              expertise and tools for the job. We treat your home with respect
              and leave your plumbing looking and working beautifully.
            </p>
          </div>
          <div className="lg:w-1/2" data-id="element-233">
            <img src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=80&w=1200&auto=format&fit=crop" alt="Close-up of a kitchen sink and faucet fixture" className="rounded-2xl shadow-2xl border-2 border-accent/20 w-full h-auto object-cover max-h-[400px]" data-id="element-234" />
          </div>
        </div>

        <div className="mb-16" data-id="element-235">
          <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 border-b border-accent/20 pb-4" data-id="element-236">
            <Home className="h-6 w-6 text-accent" data-id="element-237" />
            Residential Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-238">
            {RESIDENTIAL_SERVICES.map((service, idx) => <ServiceCard key={idx} {...service} data-id="element-239" />)}
          </div>
        </div>

        <div data-id="element-240">
          <h3 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 border-b border-accent/20 pb-4" data-id="element-241">
            <Building2 className="h-6 w-6 text-accent" data-id="element-242" />
            Commercial Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-243">
            {COMMERCIAL_SERVICES.map((service, idx) => <ServiceCard key={idx} {...service} data-id="element-244" />)}
          </div>
        </div>
      </div>
    </section>;
}
function ServiceCard({
  title,
  description,
  icon: Icon
}: {
  title: string;
  description: string;
  icon: any;
}) {
  return <Card className="bg-surface border-accent/20 hover:border-accent/60 transition-all duration-300 group hover:shadow-[0_0_30px_rgba(220,38,38,0.15)] relative overflow-hidden" data-id="element-245">
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-246">
        <Zap className="h-5 w-5 text-accent/50" data-id="element-247" />
      </div>
      <CardHeader data-id="element-248">
        <div className="h-12 w-12 rounded-lg bg-dark border border-accent/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-249">
          <Icon className="h-6 w-6 text-accent" data-id="element-250" />
        </div>
        <CardTitle className="text-xl text-white" data-id="element-251">{title}</CardTitle>
      </CardHeader>
      <CardContent data-id="element-252">
        <CardDescription className="text-slate-400 text-base mb-6" data-id="element-253">
          {description}
        </CardDescription>
        <a href="#contact" className="text-accent font-bold uppercase tracking-wider text-sm hover:text-accent-light transition-colors inline-flex items-center gap-2" data-id="element-254">
          Get Quote
          <span className="group-hover:translate-x-1 transition-transform" data-id="element-255">
            →
          </span>
        </a>
      </CardContent>
    </Card>;
}